import React from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import 'resources/styles/standard-workflow.css';

interface WorkflowPageHeaderProps {
  title: string;
  description: string;
  newActionLabel: string;
  onNew: () => void;
  saveActionLabel?: string;
  onSave?: () => void;
  // Optional second action, additive and opt-in (undefined onShare renders nothing extra —
  // every existing caller is unaffected). Added for the Design Assistant's Save-vs-Share
  // choice; any other tool can adopt it later the same way.
  shareActionLabel?: string;
  onShare?: () => void;
  disableShare?: boolean;
  savedPath?: string;
  // Text of the link shown once `savedPath` is set. Additive: defaults to the original wording, so
  // every existing caller is unchanged.
  savedActionLabel?: string;
  disableNew?: boolean;
  disableSave?: boolean;
  /** Extra controls rendered before the New/Save buttons, e.g. a CreditMeterButton. */
  extraActions?: React.ReactNode;
}

interface WorkflowPanelHeaderProps {
  title: string;
  status?: 'idle' | 'running' | 'error' | 'unavailable';
  statusLabel?: string;
}

interface WorkflowProgressHeaderProps {
  title: string;
  description?: string;
  badge?: string;
}

export type WorkflowStepStatus =
  | 'pending'
  | 'running'
  | 'complete'
  | 'skipped'
  | 'error'
  // UI-only: the phase settled operationally (backend phase_status `complete`) but its
  // automated assessment is unusable and needs qualified review — amber, never a
  // completed/safe marker. Never sent to or derived from backend orchestration.
  | 'needs_review';

export interface WorkflowProgressStep {
  label: string;
  value?: string;
  detail?: string;
  hideDetail?: boolean;
  collapsedBody?: React.ReactNode;
  expandedBody?: React.ReactNode;
  details?: Array<{
    label: string;
    value?: React.ReactNode;
  }>;
  hideHeaderValue?: boolean;
  hideHeaderValueWhenExpanded?: boolean;
  hideDetailWhenExpanded?: boolean;
  status?: WorkflowStepStatus;
  // Optional status notice rendered under the step summary (e.g. the generic "not
  // assessed — manual review required" badge). `expandedNotice` replaces it while the
  // step is expanded; omit it to show `notice` in both states. Self-hides when absent.
  notice?: React.ReactNode;
  expandedNotice?: React.ReactNode;
  onClick?: () => void;
}

interface WorkflowProgressTimelineProps {
  steps: WorkflowProgressStep[];
  autoExpandRunning?: boolean;
  leadingContent?: React.ReactNode;
  trailingContent?: React.ReactNode;
  // When true, steps never expand (no chevron, no detail/expandedBody panel) —
  // clicking a step still fires its onClick, just without the inline expand.
  disableExpand?: boolean;
}

interface WorkflowNoticeProps {
  children: React.ReactNode;
}

interface WorkflowComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (event: React.SyntheticEvent) => void;
  placeholder: string;
  disabled?: boolean;
  inputRef?: React.Ref<HTMLTextAreaElement>;
  helperText?: string;
}

interface WorkflowStreamingResponseProps {
  text: string | React.ReactElement;
  animate: boolean;
  // Fires once the reveal finishes (or immediately, if not animating). Read from a
  // ref so passing a fresh inline function each render doesn't restart the reveal.
  onComplete?: () => void;
}

export const useWorkflowChatScroll = (
  messageCount: number,
  // True while the current turn is still actively narrating/streaming (e.g.
  // ChatUI's htsThinking). Optional — a workflow whose answer always arrives
  // as its own fresh message (nothing narrates into an existing one first)
  // has nothing meaningful to pass here and can omit it.
  isGenerating = false,
): React.RefObject<HTMLDivElement> => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const previousMessageCountRef = React.useRef(0);
  const wasGeneratingRef = React.useRef(isGenerating);
  const shouldFollowLatestRef = React.useRef(true);
  const animationFrameRef = React.useRef<number | null>(null);

  const scrollToLatest = React.useCallback(() => {
    if (!shouldFollowLatestRef.current || animationFrameRef.current !== null) {
      return;
    }

    // Wait until React's DOM update has been laid out so scrollHeight includes
    // the newest streamed text. An immediate scroll here can otherwise stop a
    // few pixels short when a response wraps onto another line.
    animationFrameRef.current = window.requestAnimationFrame(() => {
      animationFrameRef.current = null;
      const scrollRegion = scrollRef.current;
      if (!scrollRegion || !shouldFollowLatestRef.current) {
        return;
      }

      // Streaming can update many times per second. An instant follow keeps the
      // latest text stable; overlapping smooth-scroll animations visibly lag.
      scrollRegion.scrollTop = scrollRegion.scrollHeight;
    });
  }, []);

  React.useEffect(() => {
    const scrollRegion = scrollRef.current;
    if (!scrollRegion) {
      return undefined;
    }

    const bottomThreshold = 64;
    const updateFollowPreference = () => {
      const distanceFromBottom =
        scrollRegion.scrollHeight -
        scrollRegion.scrollTop -
        scrollRegion.clientHeight;
      shouldFollowLatestRef.current = distanceFromBottom <= bottomThreshold;
    };

    // Also cover layout-only growth (for example, a revealed card or image)
    // that does not necessarily produce a useful text mutation.
    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => scrollToLatest());
    Array.from(scrollRegion.children).forEach((child) => {
      resizeObserver?.observe(child);
    });

    // Text deltas commonly mutate the current message instead of appending a
    // new one, so messageCount alone cannot keep a live response in view.
    const mutationObserver = new MutationObserver(() => {
      Array.from(scrollRegion.children).forEach((child) => {
        resizeObserver?.observe(child);
      });
      scrollToLatest();
    });
    mutationObserver.observe(scrollRegion, {
      childList: true,
      characterData: true,
      subtree: true,
    });

    scrollRegion.addEventListener('scroll', updateFollowPreference, {
      passive: true,
    });
    scrollToLatest();

    return () => {
      scrollRegion.removeEventListener('scroll', updateFollowPreference);
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
    // Workflow chat regions are conditionally rendered after the first message.
    // Re-run when the message count changes so a hook first invoked against an
    // empty state can attach its listeners once the scroll region exists.
  }, [messageCount, scrollToLatest]);

  // New messages and turn completion are explicit follow triggers. They still
  // respect a user who has scrolled upward; returning within the threshold
  // automatically opts back into following subsequent updates.
  React.useEffect(() => {
    const isNewMessage = messageCount !== previousMessageCountRef.current;
    previousMessageCountRef.current = messageCount;

    const justFinishedGenerating = wasGeneratingRef.current && !isGenerating;
    wasGeneratingRef.current = isGenerating;

    if (!isNewMessage && !justFinishedGenerating) return;

    scrollToLatest();
  }, [messageCount, isGenerating, scrollToLatest]);

  return scrollRef;
};

// Attach to the two-column grid (assistant card + progress card) that wraps a
// workflow chat. When a conversation goes from empty to started (the page's
// own scroll position is still at the top from load), this scrolls the page
// so the grid — and the composer near its bottom — comes into view instead of
// staying cut off below the fold.
export const useScrollWorkflowIntoView = (
  hasConversationStarted: boolean,
): React.RefObject<HTMLDivElement> => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const wasStartedRef = React.useRef(false);

  React.useEffect(() => {
    if (hasConversationStarted && !wasStartedRef.current) {
      // requestAnimationFrame gives the browser one extra tick to finish
      // laying out the just-committed DOM change (e.g. the empty-state ->
      // active-conversation swap) before we measure it.
      window.requestAnimationFrame(() => {
        const container = containerRef.current;
        if (!container) return;

        // Scroll the window directly from the element's measured position
        // rather than relying on scrollIntoView's ancestor-walking, which
        // behaves inconsistently when a page has its own nested scroll
        // containers alongside the window.
        const rect = container.getBoundingClientRect();
        const targetTop =
          window.scrollY + rect.bottom - window.innerHeight + 24;

        window.scrollTo({
          top: Math.max(0, targetTop),
          behavior: 'smooth',
        });
      });
    }

    wasStartedRef.current = hasConversationStarted;
  }, [hasConversationStarted]);

  return containerRef;
};

const renderWorkflowStreamingText = (text: string | React.ReactElement) => {
  if (typeof text !== 'string') return text;

  const streamingMatch = text.match(/^(.*?)(\.\.\.)$/);
  if (!streamingMatch) {
    return (
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
    );
  }

  const [, content] = streamingMatch;

  return (
    <>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
      <span
        aria-hidden='true'
        style={{
          display: 'inline-block',
          whiteSpace: 'nowrap',
          verticalAlign: 'baseline',
        }}
      >
        {[0, 1, 2].map((dotIndex) => (
          <span
            key={dotIndex}
            className='streaming-dot'
            style={{ animationDelay: `${dotIndex * 0.18}s` }}
          >
            .
          </span>
        ))}
      </span>
    </>
  );
};

export const WorkflowStreamingResponse: React.FC<
  WorkflowStreamingResponseProps
> = ({ text, animate, onComplete }) => {
  const onCompleteRef = React.useRef(onComplete);
  onCompleteRef.current = onComplete;

  const shouldAnimateInitially =
    animate &&
    typeof text === 'string' &&
    Boolean(text) &&
    text !== 'Thinking...' &&
    !text.endsWith('...');
  const [visibleText, setVisibleText] = React.useState<
    string | React.ReactElement
  >(shouldAnimateInitially ? '' : text);

  React.useEffect(() => {
    const shouldAnimate =
      animate &&
      typeof text === 'string' &&
      Boolean(text) &&
      text !== 'Thinking...' &&
      !text.endsWith('...');

    if (!shouldAnimate || typeof text !== 'string') {
      setVisibleText(text);
      onCompleteRef.current?.();
      return undefined;
    }

    setVisibleText('');
    const tokens = text.match(/\S+|\s+/g) || [text];
    let tokenIndex = 0;
    const intervalId = window.setInterval(() => {
      tokenIndex = Math.min(tokenIndex + 3, tokens.length);
      setVisibleText(tokens.slice(0, tokenIndex).join(''));

      if (tokenIndex >= tokens.length) {
        window.clearInterval(intervalId);
        onCompleteRef.current?.();
      }
    }, 18);

    return () => window.clearInterval(intervalId);
  }, [animate, text]);

  return <>{renderWorkflowStreamingText(visibleText)}</>;
};

interface WorkflowSequentialRevealProps {
  // Render children at all — true once this item's turn has arrived or passed.
  ready: boolean;
  // True only while THIS item is the current one counting down to reveal the next.
  advancing: boolean;
  onRevealed?: () => void;
  delayMs?: number;
  children: React.ReactNode;
}

// Non-text counterpart to WorkflowStreamingResponse: gates a whole block (a list
// row, a card) into the same one-at-a-time reveal sequence, for content that has
// no free text to type out. Sits inert once revealed — never re-hides.
export const WorkflowSequentialReveal: React.FC<
  WorkflowSequentialRevealProps
> = ({ ready, advancing, onRevealed, delayMs = 220, children }) => {
  const onRevealedRef = React.useRef(onRevealed);
  onRevealedRef.current = onRevealed;

  React.useEffect(() => {
    if (!advancing) return undefined;
    const timerId = window.setTimeout(() => {
      onRevealedRef.current?.();
    }, delayMs);
    return () => window.clearTimeout(timerId);
  }, [advancing, delayMs]);

  if (!ready) return null;
  return <>{children}</>;
};

const AssistantIcon = () => (
  <span className='standard-workflow-bot-icon' aria-hidden='true'>
    <svg
      width='18'
      height='18'
      viewBox='0 0 24 24'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M7 9H17M8 15H16M9 5V3M15 5V3M6 20H18C19.1046 20 20 19.1046 20 18V8C20 6.89543 19.1046 6 18 6H6C4.89543 6 4 6.89543 4 8V18C4 19.1046 4.89543 20 6 20Z'
        stroke='currentColor'
        strokeWidth='1.8'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  </span>
);

export const WorkflowPageHeader: React.FC<WorkflowPageHeaderProps> = ({
  title,
  description,
  newActionLabel,
  onNew,
  saveActionLabel = 'Save Result',
  onSave,
  shareActionLabel = 'Share',
  onShare,
  disableShare = false,
  savedPath,
  savedActionLabel = 'View Saved',
  disableNew = false,
  disableSave = false,
  extraActions,
}) => (
  <header className='standard-workflow-page-header'>
    <div>
      <h1>{title}</h1>
      <p>{description}</p>
    </div>
    <div className='standard-workflow-page-actions'>
      {extraActions}
      <button
        type='button'
        className='standard-workflow-secondary-btn'
        onClick={onNew}
        disabled={disableNew}
      >
        {newActionLabel}
      </button>
      {savedPath ? (
        <Link className='standard-workflow-primary-btn' to={savedPath}>
          {savedActionLabel}
        </Link>
      ) : onSave ? (
        <button
          type='button'
          className='standard-workflow-primary-btn'
          onClick={onSave}
          disabled={disableSave}
        >
          {saveActionLabel}
        </button>
      ) : null}
      {onShare ? (
        <button
          type='button'
          className='standard-workflow-secondary-btn'
          onClick={onShare}
          disabled={disableShare}
        >
          {shareActionLabel}
        </button>
      ) : null}
    </div>
  </header>
);

export const WorkflowAssistantHeader: React.FC<WorkflowPanelHeaderProps> = ({
  title,
  status = 'idle',
  statusLabel,
}) => {
  const label =
    statusLabel ||
    (status === 'running'
      ? 'Responding'
      : status === 'error'
      ? 'Error'
      : status === 'unavailable'
      ? 'Unavailable'
      : 'Idle');

  return (
    <div className='standard-workflow-panel-header'>
      <div className='standard-workflow-panel-title'>
        <AssistantIcon />
        <h2>{title}</h2>
      </div>
      <div
        className={`standard-workflow-status ${status}`}
        role='status'
        aria-live='polite'
      >
        <span aria-hidden='true' />
        {label}
      </div>
    </div>
  );
};

export const WorkflowProgressHeader: React.FC<WorkflowProgressHeaderProps> = ({
  title,
  description,
  badge,
}) => (
  <div className='standard-workflow-progress-header'>
    <div className='standard-workflow-progress-heading'>
      <h2>{title}</h2>
      {badge && <span className='standard-workflow-badge'>{badge}</span>}
    </div>
    {description && <p>{description}</p>}
  </div>
);

export const WorkflowNotice: React.FC<WorkflowNoticeProps> = ({ children }) => (
  <div className='standard-workflow-notice' role='note'>
    <span className='standard-workflow-notice-icon' aria-hidden='true'>
      i
    </span>
    <div className='standard-workflow-notice-content'>{children}</div>
  </div>
);

interface WorkflowCreditWarningProps {
  remaining: number | null;
  cost: number;
}

// Stand-alone yellow notice shown when the team can't cover one run of the workflow.
export const WorkflowCreditWarning: React.FC<WorkflowCreditWarningProps> = ({
  remaining,
  cost,
}) => (
  <div className='standard-workflow-credit-warning' role='alert'>
    <svg
      className='standard-workflow-credit-warning-icon'
      width='20'
      height='20'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <path d='M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z' />
      <line x1='12' y1='9' x2='12' y2='13' />
      <line x1='12' y1='17' x2='12.01' y2='17' />
    </svg>
    <div>
      <strong>Do not have enough Credits to run</strong>
      <p>
        You have {remaining ?? 0} credit{remaining === 1 ? '' : 's'} remaining;
        this workflow costs {cost} credit{cost === 1 ? '' : 's'} per run.
      </p>
    </div>
  </div>
);

const getDefaultStepValue = (status: WorkflowStepStatus) => {
  switch (status) {
    case 'running':
      return 'In progress';
    case 'complete':
      return 'Complete';
    case 'skipped':
      return 'Skipped';
    case 'error':
      return 'Needs attention';
    case 'needs_review':
      return 'Needs review';
    default:
      return 'Pending';
  }
};

const WorkflowDetailIcon: React.FC<{ label: string }> = ({ label }) => {
  const normalizedLabel = label.toLowerCase();
  const path = normalizedLabel.includes('product')
    ? 'M8 1.5 13.5 4.5V11.5L8 14.5 2.5 11.5V4.5L8 1.5ZM2.75 4.75 8 7.75M13.25 4.75 8 7.75M8 7.75V14.25'
    : normalizedLabel.includes('note') ||
      normalizedLabel.includes('section') ||
      normalizedLabel.includes('chapter')
    ? 'M2 3.25C3.75 2.75 5.75 2.75 8 4V13C5.75 11.75 3.75 11.75 2 12.25V3.25ZM14 3.25C12.25 2.75 10.25 2.75 8 4V13C10.25 11.75 12.25 11.75 14 12.25V3.25Z'
    : normalizedLabel.includes('cross') ||
      normalizedLabel.includes('ruling') ||
      normalizedLabel.includes('license')
    ? 'M2 6H14M3.5 6V12.5M6.5 6V12.5M9.5 6V12.5M12.5 6V12.5M2 13.5H14M8 1.5 14 5H2L8 1.5Z'
    : normalizedLabel.includes('country') ||
      normalizedLabel.includes('origin') ||
      normalizedLabel.includes('destination')
    ? 'M8 14.5A6.5 6.5 0 1 0 8 1.5a6.5 6.5 0 0 0 0 13ZM1.75 8H14.25M8 1.75C9.75 3.5 10.5 5.5 10.5 8S9.75 12.5 8 14.25C6.25 12.5 5.5 10.5 5.5 8S6.25 3.5 8 1.75Z'
    : 'M3 1.75H9.5L13 5.25V14.25H3V1.75ZM9.5 1.75V5.25H13M5.5 8H10.5M5.5 10.5H10.5';

  return (
    <svg
      width='18'
      height='18'
      viewBox='0 0 16 16'
      fill='none'
      aria-hidden='true'
    >
      <path
        d={path}
        stroke='currentColor'
        strokeWidth='1.4'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
};

const normalizeWorkflowDetailValue = (value: React.ReactNode) =>
  typeof value === 'string'
    ? value
        .replace(/\r\n/g, '\n')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n[ \t]*\n+/g, '\n')
        .trim()
    : value;

const renderWorkflowInlineValue = (value: string) =>
  value.split(/(https?:\/\/[^\s]+)/g).map((part, index) =>
    /^https?:\/\/[^\s]+$/.test(part) ? (
      <a
        href={part}
        key={`${part}-${index}`}
        target='_blank'
        rel='noopener noreferrer'
      >
        {part}
      </a>
    ) : (
      part
    ),
  );

const renderWorkflowDetailValue = (
  value: React.ReactNode,
  label: string,
  isExpanded: boolean,
) => {
  if (typeof value !== 'string') return value;

  const normalizedLabel = label.toLowerCase();
  const isCrossRulings =
    normalizedLabel.includes('cross') || normalizedLabel.includes('ruling');
  const urls = isCrossRulings ? value.match(/https?:\/\/[^\s]+/g) || [] : [];

  if (urls.length > 0) {
    const visibleUrls = isExpanded ? urls : urls.slice(0, 3);

    return (
      <div className='standard-workflow-link-list'>
        {visibleUrls.map((url, index) => (
          <a
            href={url}
            key={`${url}-${index}`}
            target='_blank'
            rel='noopener noreferrer'
          >
            {url}
          </a>
        ))}
        {!isExpanded && urls.length > visibleUrls.length && (
          <span aria-hidden='true'>...</span>
        )}
      </div>
    );
  }

  const isSectionOrChapterNotes =
    normalizedLabel.includes('section') || normalizedLabel.includes('chapter');

  if (!isSectionOrChapterNotes) return renderWorkflowInlineValue(value);

  const lines = value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  return (
    <div className='standard-workflow-formatted-notes'>
      {lines.map((line, index) => {
        const cleanLine = line.replace(/\*\*/g, '');
        const isReferenceHeading = /^(?:[IVXLCDM]+|\d+)(?:-\d+)?:?$/i.test(
          cleanLine,
        );
        const isSectionHeading =
          !isReferenceHeading &&
          cleanLine.length < 100 &&
          /[A-Z]/.test(cleanLine) &&
          /^[A-Z0-9\s;,&:/()\-.]+$/.test(cleanLine);
        const isListItem = /^(?:\d+[.)]|\([a-z0-9]+\)|[a-z][.)])\s+/i.test(
          cleanLine,
        );

        if (isSectionHeading) {
          return (
            <h4 key={`${cleanLine}-${index}`}>
              {renderWorkflowInlineValue(cleanLine)}
            </h4>
          );
        }

        if (isReferenceHeading) {
          return (
            <h5 key={`${cleanLine}-${index}`}>
              {renderWorkflowInlineValue(cleanLine)}
            </h5>
          );
        }

        return (
          <p
            className={isListItem ? 'note-list-item' : undefined}
            key={`${cleanLine}-${index}`}
          >
            {renderWorkflowInlineValue(cleanLine)}
          </p>
        );
      })}
    </div>
  );
};

export const WorkflowProgressTimeline: React.FC<
  WorkflowProgressTimelineProps
> = ({
  steps,
  autoExpandRunning = false,
  leadingContent,
  trailingContent,
  disableExpand = false,
}) => {
  const [expandedStep, setExpandedStep] = React.useState<string | null>(null);
  const [expandedDetail, setExpandedDetail] = React.useState<string | null>(
    null,
  );
  const [expandedContent, setExpandedContent] = React.useState<string | null>(
    null,
  );
  const autoExpandedStepRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!autoExpandRunning) return;

    const runningStepIndex = steps.findIndex(
      (step) => step.status === 'running',
    );
    if (runningStepIndex < 0) {
      autoExpandedStepRef.current = null;
      return;
    }

    const runningStep = steps[runningStepIndex];
    const runningStepKey = `${runningStep.label}-${runningStepIndex}`;
    if (autoExpandedStepRef.current === runningStepKey) return;

    autoExpandedStepRef.current = runningStepKey;
    setExpandedDetail(null);
    setExpandedContent(null);
    setExpandedStep(runningStepKey);
  }, [autoExpandRunning, steps]);

  return (
    <div className='standard-workflow-timeline'>
      {leadingContent && (
        <div className='standard-workflow-timeline-leading'>
          <span aria-hidden='true' />
          <div className='standard-workflow-timeline-leading-content'>
            {leadingContent}
          </div>
        </div>
      )}
      {steps.map((step, index) => {
        const status = step.status || 'pending';
        const stepKey = `${step.label}-${index}`;
        const details = (step.details || []).filter(
          (item) =>
            item.value !== undefined &&
            item.value !== null &&
            item.value !== '',
        );
        const canExpand =
          !disableExpand &&
          (details.length > 0 ||
            Boolean(step.collapsedBody) ||
            Boolean(step.expandedBody));
        const isExpanded = expandedStep === stepKey;
        const notice =
          isExpanded && step.expandedNotice !== undefined
            ? step.expandedNotice
            : step.notice;
        const noticeBlock = notice ? (
          <div className='standard-workflow-step-notice'>{notice}</div>
        ) : null;
        const primaryDetail = details[0];
        const nestedDetails = details.slice(1);
        const handleClick = () => {
          step.onClick?.();

          if (canExpand) {
            setExpandedDetail(null);
            setExpandedContent(null);
            setExpandedStep((current) =>
              current === stepKey ? null : stepKey,
            );
          }
        };
        const content = (
          <>
            <div className='standard-workflow-step-heading'>
              <span>{step.label}</span>
              <span className='standard-workflow-step-actions'>
                {!step.hideHeaderValue &&
                  !(isExpanded && step.hideHeaderValueWhenExpanded) && (
                  <strong>{step.value || getDefaultStepValue(status)}</strong>
                )}
                {canExpand && (
                  <svg
                    className='standard-workflow-step-chevron'
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                    aria-hidden='true'
                  >
                    <path
                      d={isExpanded ? 'M4 6L8 10L12 6' : 'M10 12L6 8L10 4'}
                      stroke='currentColor'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                )}
              </span>
            </div>
            {!step.hideDetail &&
              !(isExpanded && step.hideDetailWhenExpanded) && (
              <p>{step.detail || 'Awaiting information...'}</p>
            )}
          </>
        );

        return (
          <div className={`standard-workflow-step ${status}`} key={stepKey}>
            <div className='standard-workflow-step-marker' aria-hidden='true'>
              <span />
              {index < steps.length - 1 && <i />}
            </div>
            {step.onClick || canExpand ? (
              <div
                className={`standard-workflow-step-card-shell${
                  isExpanded ? ' expanded' : ''
                }${step.collapsedBody ? ' has-collapsed-body' : ''}${
                  expandedContent?.startsWith(`${stepKey}-`)
                    ? ' reading-more'
                    : ''
                }`}
              >
                <button
                  type='button'
                  className='standard-workflow-step-summary'
                  onClick={handleClick}
                  aria-expanded={canExpand ? isExpanded : undefined}
                >
                  {content}
                </button>
                {noticeBlock}
                {!isExpanded && step.collapsedBody && (
                  <div className='standard-workflow-step-collapsed-body'>
                    {step.collapsedBody}
                  </div>
                )}
                {canExpand && isExpanded && (
                  <div
                    className={`standard-workflow-step-details${
                      step.expandedBody ? ' custom-body' : ''
                    }`}
                  >
                    {step.expandedBody ? (
                      step.expandedBody
                    ) : primaryDetail ? (
                      <>
                        <div className='standard-workflow-step-primary-detail'>
                          <span>{primaryDetail.label}:</span>
                          <strong>{primaryDetail.value}</strong>
                        </div>
                        <div className='standard-workflow-step-subdetails'>
                          {nestedDetails.map((item, detailIndex) => {
                            const detailKey = `${stepKey}-${item.label}-${detailIndex}`;
                            const isDetailExpanded =
                              expandedDetail === detailKey;
                            const normalizedValue =
                              normalizeWorkflowDetailValue(item.value);
                            const isCrossRulings =
                              item.label.toLowerCase().includes('cross') ||
                              item.label.toLowerCase().includes('ruling');
                            const rulingUrls =
                              isCrossRulings &&
                              typeof normalizedValue === 'string'
                                ? normalizedValue.match(/https?:\/\/[^\s]+/g) ||
                                  []
                                : [];
                            const isLongDetail = rulingUrls.length
                              ? rulingUrls.length > 3
                              : typeof normalizedValue === 'string' &&
                                (normalizedValue.length > 240 ||
                                  normalizedValue.split('\n').length > 6);
                            const isContentExpanded =
                              expandedContent === detailKey;

                            return (
                              <div
                                className={`standard-workflow-step-subdetail${
                                  isDetailExpanded ? ' expanded' : ''
                                }`}
                                key={detailKey}
                              >
                                <button
                                  type='button'
                                  onClick={() => {
                                    setExpandedContent(null);
                                    setExpandedDetail((current) =>
                                      current === detailKey ? null : detailKey,
                                    );
                                  }}
                                  aria-expanded={isDetailExpanded}
                                >
                                  <WorkflowDetailIcon label={item.label} />
                                  <span>{item.label}</span>
                                  <svg
                                    width='16'
                                    height='16'
                                    viewBox='0 0 16 16'
                                    fill='none'
                                    aria-hidden='true'
                                  >
                                    <path
                                      d={
                                        isDetailExpanded
                                          ? 'M4 6L8 10L12 6'
                                          : 'M10 12L6 8L10 4'
                                      }
                                      stroke='currentColor'
                                      strokeWidth='1.5'
                                      strokeLinecap='round'
                                      strokeLinejoin='round'
                                    />
                                  </svg>
                                </button>
                                <div
                                  className={`standard-workflow-step-subdetail-value${
                                    isContentExpanded ? ' content-expanded' : ''
                                  }`}
                                  aria-hidden={!isDetailExpanded}
                                >
                                  <div
                                    className={
                                      isLongDetail &&
                                      !isContentExpanded &&
                                      rulingUrls.length === 0
                                        ? 'standard-workflow-detail-content truncated'
                                        : 'standard-workflow-detail-content'
                                    }
                                  >
                                    {renderWorkflowDetailValue(
                                      normalizedValue,
                                      item.label,
                                      isContentExpanded,
                                    )}
                                  </div>
                                  {isLongDetail && (
                                    <button
                                      type='button'
                                      className='standard-workflow-detail-more'
                                      onClick={() =>
                                        setExpandedContent((current) =>
                                          current === detailKey
                                            ? null
                                            : detailKey,
                                        )
                                      }
                                      aria-expanded={isContentExpanded}
                                    >
                                      {isContentExpanded
                                        ? 'Read Less'
                                        : 'Read More'}
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    ) : null}
                  </div>
                )}
              </div>
            ) : (
              <div className='standard-workflow-step-card'>
                {content}
                {noticeBlock}
              </div>
            )}
          </div>
        );
      })}
      {trailingContent && (
        <div className='standard-workflow-timeline-trailing'>
          <span aria-hidden='true' />
          <div className='standard-workflow-timeline-trailing-content'>
            {trailingContent}
          </div>
        </div>
      )}
    </div>
  );
};

export const WorkflowComposer: React.FC<WorkflowComposerProps> = ({
  value,
  onChange,
  onSubmit,
  placeholder,
  disabled = false,
  inputRef,
  helperText,
}) => {
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);

  const setTextareaRef = React.useCallback(
    (node: HTMLTextAreaElement | null) => {
      textareaRef.current = node;

      if (typeof inputRef === 'function') {
        inputRef(node);
      } else if (inputRef) {
        (
          inputRef as React.MutableRefObject<HTMLTextAreaElement | null>
        ).current = node;
      }
    },
    [inputRef],
  );

  React.useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = '34px';

    const computedMaxHeight = Number.parseFloat(
      window.getComputedStyle(textarea).maxHeight,
    );
    const maxHeight = Number.isFinite(computedMaxHeight)
      ? computedMaxHeight
      : 120;
    const nextHeight = Math.min(textarea.scrollHeight, maxHeight);

    textarea.style.height = `${Math.max(34, nextHeight)}px`;
    textarea.style.overflowY =
      textarea.scrollHeight > maxHeight + 1 ? 'auto' : 'hidden';
  }, [value]);

  return (
    <form className='standard-workflow-composer' onSubmit={onSubmit}>
      <div className='standard-workflow-composer-field'>
        <textarea
          ref={setTextareaRef}
          rows={1}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              onSubmit(event);
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          aria-label={placeholder}
        />
        <button
          type='submit'
          disabled={disabled || !value.trim()}
          aria-label='Send message'
        >
          <svg
            width='20'
            height='20'
            viewBox='0 0 24 24'
            fill='currentColor'
            xmlns='http://www.w3.org/2000/svg'
            aria-hidden='true'
          >
            <path d='M2.01 21 23 12 2.01 3 2 10l15 2-15 2z' />
          </svg>
        </button>
      </div>
      {helperText && (
        <p className='standard-workflow-composer-helper'>{helperText}</p>
      )}
    </form>
  );
};
